import { apiClient } from './apiClient';

/** The five support departments, plus legacy 'technical' (reads as Web Dev).
 *  Which ones a new ticket may use depends on the backend's routing flag —
 *  ask `getCategories()`, never assume. */
export type SupportCategory =
  | 'technical'
  | 'seo'
  | 'web_dev'
  | 'ai'
  | 'ads'
  | 'communications';

export interface SupportCategoryOption {
  key: SupportCategory;
  label: string;
}

export interface SupportCategoriesResponse {
  routing_enabled: boolean;
  categories: SupportCategoryOption[];
}

export type SupportPriority = 'low' | 'medium' | 'high';

/** Which UI surface filed the request — the /support page's own form, or
 *  the global feedback-widget button in the header. Presentation-only
 *  provenance, never inferred by the AI draft assistant. */
export type SupportEntryPoint = 'support_page' | 'feedback_widget';

export interface SupportRequestInput {
  category: SupportCategory;
  message: string;
  /** Current workspace/tenant (domain string), for routing context. */
  workspace?: string;
  /** Optional overrides — e.g. from the AI-assisted intake modal's review
   *  form. Without them the backend derives a title from `message` and
   *  defaults priority to "medium", same as before these existed. */
  title?: string;
  priority?: SupportPriority;
  entryPoint?: SupportEntryPoint;
}

export interface SupportRequestResult {
  /** False only when the request was neither saved NOR delivered to Slack.
   *  This used to be unconditionally true, so a lost request was
   *  indistinguishable from a sent one. */
  ok: boolean;
  /** Whether a ticket row was actually written. */
  persisted?: boolean;
  /** The created ticket's id, or null when persistence failed. */
  ticket_id?: string | null;
  /** The internal team the request was routed to (e.g. "dev/AI team"). */
  team: string;
  /** False if Slack was disabled/misconfigured (request is still logged). */
  notified: boolean;
}

export const supportService = {
  /** What a new-ticket form may offer right now (GET /support/categories). */
  async getCategories(): Promise<SupportCategoriesResponse> {
    const { data } = await apiClient.get<SupportCategoriesResponse>('/support/categories');
    return data;
  },

  async submit(input: SupportRequestInput): Promise<SupportRequestResult> {
    const { data } = await apiClient.post<SupportRequestResult>('/support/request', {
      category: input.category,
      message: input.message,
      workspace: input.workspace,
      title: input.title,
      priority: input.priority,
      entry_point: input.entryPoint,
    });
    return data;
  },
};
